import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0')
  return [`${hour}:00`, `${hour}:30`]
}).flat()

export default function QuietHourModal({ isOpen, onClose, onSave, editingQuietHour, existingQuietHours = [] }) {
  const { t } = useTranslation('settings')
  const [name, setName] = useState('')
  const [start, setStart] = useState('22:00')
  const [end, setEnd] = useState('07:00')
  const [days, setDays] = useState([0, 1, 2, 3, 4])
  const [error, setError] = useState(null)

  // Get day names and labels from translations
  const dayNames = t('quietHours.days.short', { returnObjects: true })
  const DAY_OPTIONS = dayNames.map((label, index) => ({ value: index, label }))

  // Helper: Convert HH:MM to minutes
  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number)
    return h * 60 + m
  }

  // Helper: Check if two ranges overlap
  const rangesOverlap = (start1, end1, start2, end2) => {
    const overnight1 = start1 >= end1
    const overnight2 = start2 >= end2

    const getRanges = (s, e, overnight) => {
      if (overnight) return [[s, 1440], [0, e]]
      return [[s, e]]
    }

    const ranges1 = getRanges(start1, end1, overnight1)
    const ranges2 = getRanges(start2, end2, overnight2)

    for (const r1 of ranges1) {
      for (const r2 of ranges2) {
        if (r1[0] < r2[1] && r2[0] < r1[1]) return true
      }
    }
    return false
  }

  // Check for overlaps with existing quiet hours
  const checkOverlaps = () => {
    if (days.length === 0) return null

    const myStart = timeToMinutes(start)
    const myEnd = timeToMinutes(end)
    const myDays = new Set(days)

    for (const qh of existingQuietHours) {
      // Skip self when editing
      if (editingQuietHour && qh.id === editingQuietHour.id) continue
      // Skip disabled
      if (!qh.enabled) continue

      // Check for common days
      const theirDays = new Set(qh.days || [])
      const commonDays = [...myDays].filter(d => theirDays.has(d))
      if (commonDays.length === 0) continue

      const theirStart = timeToMinutes(qh.start)
      const theirEnd = timeToMinutes(qh.end)

      if (rangesOverlap(myStart, myEnd, theirStart, theirEnd)) {
        return qh.name || `${t('quietHours.card.defaultName')} ${qh.start}-${qh.end}`
      }
    }
    return null
  }

  const overlappingWith = checkOverlaps()

  useEffect(() => {
    if (editingQuietHour) {
      setName(editingQuietHour.name || '')
      setStart(editingQuietHour.start)
      setEnd(editingQuietHour.end)
      setDays(editingQuietHour.days)
    } else {
      setName('')
      setStart('22:00')
      setEnd('07:00')
      setDays([0, 1, 2, 3, 4])
    }
    setError(null)
  }, [editingQuietHour, isOpen])

  // Calculate total blocked minutes for a day
  const calculateBlockedMinutes = (startTime, endTime) => {
    const startMins = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1])
    const endMins = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1])

    if (startMins < endMins) {
      return endMins - startMins
    } else {
      // Overnight
      return (24 * 60 - startMins) + endMins
    }
  }

  // Calculate if this would leave less than 2h/week free (warning threshold)
  const wouldBlockTooMuch = () => {
    if (days.length === 0) return false

    const blockedPerDay = calculateBlockedMinutes(start, end)
    const totalBlockedMinutes = blockedPerDay * days.length
    const totalWeekMinutes = 7 * 24 * 60
    const freeMinutes = totalWeekMinutes - totalBlockedMinutes

    // Warning threshold: less than 2 hours free per week
    return freeMinutes < 120
  }

  const isWarning = wouldBlockTooMuch()

  const toggleDay = (day) => {
    setDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    )
  }

  const selectWeekdays = () => setDays([0, 1, 2, 3, 4])
  const selectWeekend = () => setDays([5, 6])
  const selectAll = () => setDays([0, 1, 2, 3, 4, 5, 6])

  const handleSave = () => {
    if (days.length === 0) {
      setError(t('quietHours.modal.errors.noDays'))
      return
    }
    if (start === end) {
      setError(t('quietHours.modal.errors.sameTime'))
      return
    }

    onSave({
      id: editingQuietHour?.id || crypto.randomUUID(),
      name: name.trim() || null,
      start,
      end,
      days,
      enabled: editingQuietHour?.enabled ?? true,
      source: 'manual'
    })
  }

  if (!isOpen) return null

  const isOvernight = start > end

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingQuietHour ? t('quietHours.modal.titleEdit') : t('quietHours.modal.titleAdd')}
          </h3>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('quietHours.modal.name.label')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('quietHours.modal.name.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Time inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('quietHours.modal.time.from')}</label>
              <select
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {TIME_OPTIONS.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('quietHours.modal.time.to')}</label>
              <select
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {TIME_OPTIONS.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Overnight info */}
          {isOvernight && (
            <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-800">
                {t('quietHours.modal.overnight', { start, end })}
              </p>
            </div>
          )}

          {/* Overlap warning */}
          {overlappingWith && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-red-800">
                {t('quietHours.modal.overlap', { name: overlappingWith })}
              </p>
            </div>
          )}

          {/* Weekdays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('quietHours.modal.weekdaysLabel')}</label>
            <div className="flex flex-wrap gap-2">
              {DAY_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleDay(value)}
                  className={`w-10 h-10 rounded-lg border-2 font-medium text-sm transition-colors ${
                    days.includes(value)
                      ? 'border-primary-600 bg-primary-50 text-primary-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Quick select */}
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={selectWeekdays} className="text-xs text-primary-600 hover:underline">
                {t('quietHours.days.weekdays')}
              </button>
              <button type="button" onClick={selectWeekend} className="text-xs text-primary-600 hover:underline">
                {t('quietHours.days.weekend')}
              </button>
              <button type="button" onClick={selectAll} className="text-xs text-primary-600 hover:underline">
                {t('quietHours.days.all')}
              </button>
            </div>
          </div>

          {/* Warning for too much blocking */}
          {isWarning && !error && (
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-amber-800">
                {t('quietHours.modal.warning')}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
          >
            {t('quietHours.modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!!overlappingWith}
            className="px-4 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('quietHours.modal.save')}
          </button>
        </div>
      </div>
    </div>
  )
}
